// src/features/checklists/ChecklistLogsTableDepricated.tsx
import "ag-grid-enterprise";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Stack, Text, Title } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-enterprise";
import { useGetChecklistLogsQuery } from "../../../../openapi";
import { selectProjectNo } from "../../projectSelectors";
import { IChecklistLogApi } from "@mcapps/shared/schemas/checklistLogSchema";
import { FailedAlert } from "../../../components";
import { IRootState } from "../../../store";
import { useAppSelector } from "../../../hooks";
import { formatDateToNorwegianDate } from "../admin/selectors";

export const ChecklistLogsTableDepricated: React.FC = () => {
  const projectNo = useAppSelector(selectProjectNo);

  const checklistId: number | null = useSelector((state: IRootState) => state.checklistReducer?.checklist?.id ?? null);

  const skip = !projectNo || !checklistId;

  const {
    data: logs = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetChecklistLogsQuery({ projectNo: projectNo ?? 0, checklistId: checklistId ?? 0 }, { skip });

  const columnDefs = useMemo<ColDef<IChecklistLogApi>[]>(() => {
    const fmtDate = (v: unknown) => {
      if (v == null) return "";
      const d = v instanceof Date ? v : new Date(String(v));
      if (Number.isNaN(d.getTime())) return String(v);
      return formatDateToNorwegianDate(d as any);
    };

    return [
      {
        headerName: "Tid",
        field: "createdAt" as any,
        valueFormatter: (p) => fmtDate(p.value),
        sort: "desc",
        width: 180,
      },
      {
        headerName: "Bruker",
        valueGetter: (p) => p.data?.user?.name ?? "",
        width: 170,
      },
      { headerName: "Felt", field: "column" as any, width: 180 },
      { headerName: "Gammel verdi", field: "oldValue" as any, minWidth: 100 },
      { headerName: "Ny verdi", field: "newValue" as any, minWidth: 100 },
      {
        headerName: "Rad",
        flex: 1,
        valueGetter: (p) => {
          const r = p.data?.rowId;
          if (!r) return "";
          const idPart = r.itemId != null ? String(r.itemId) : "";
          const titlePart = r.title ?? "";
          if (!idPart && !titlePart) return "";
          if (!idPart) return titlePart;
          if (!titlePart) return idPart;
          return `${idPart} - ${titlePart}`;
        },
      },
    ];
  }, []);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      filter: true, // inbygde filter i AG Grid
      resizable: true,
    };
  }, []);

  return (
    <Stack gap="sm" h="100%" p={"sm"}>
      {isError === true && <FailedAlert />}
      <Box>
        <Title order={4}>Endringslogg</Title>
        <Text size="sm" c="dimmed">
          {isFetching ? "Oppdaterer…" : `${logs.length} rader`}
        </Text>
      </Box>

      <Box className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
        <AgGridReact<IChecklistLogApi>
          rowData={logs}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows
          suppressCellFocus
          rowSelection="single"
        />
      </Box>
    </Stack>
  );
};
